import { useRef, useState, useEffect, useMemo } from "react";

import { useDocumentModal } from "../../common/CustomModal/CustomModalContext";
import HeaderSection from "../../DocPage/new-tab/headerSection";

import { PhotoDetailedCard } from "./components/photo-detailed-view-card";

const CARD_WIDTH = 255;
const GAP = 5;

const NewPhotoDetailedTab = ({ data, refetchData }) => {
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const { showDocumentModal } = useDocumentModal();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateLayout = () => {
      const containerWidth = el.getBoundingClientRect().width;
      const perRow = Math.floor((containerWidth + GAP) / (CARD_WIDTH + GAP));
      const validPerRow = perRow || 1;

      setCardsPerRow(validPerRow);
    };

    const observer = new ResizeObserver(updateLayout);
    observer.observe(el);
    updateLayout();

    return () => observer.disconnect();
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];

    const flatDocs = [];
    data?.results?.forEach((section) => {
      const sectionName = section.page?.name || "Unsorted";
      const isPanels = section?.page?.panels || false;
      const sectionIcon =
        section.page?.page_icon ||
        "https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg";

      const photo = section;
      const panelInfo = isPanels
        ? {
            panelName: photo?.panel?.panel_name || "",
            panelId: photo?.panel?.panel_id || null,
            specialty: photo?.panel?.specialty || null,
          }
        : null;

      flatDocs.push({
        ...photo,
        sectionName,
        sectionIcon,
        isPanels,
        panelName: panelInfo?.panelName || null,
        panelId: panelInfo?.panelId || null,
        specialty: panelInfo?.specialty || null,
      });
    });

    const result = [];
    let idx = 0;
    while (idx < flatDocs.length) {
      const rowDocs = flatDocs.slice(idx, idx + cardsPerRow);
      const sectionGroups = {};

      rowDocs.forEach((photo) => {
        const key = photo.sectionName;
        if (!sectionGroups[key]) {
          sectionGroups[key] = {
            icon: photo.sectionIcon,
            isPanels: photo.isPanels,
            panelName: photo.panelName,
            panelId: photo.panelId,
            specialty: photo.specialty,
            photos: [],
          };
        }
        sectionGroups[key].photos.push(photo);
      });

      result.push(sectionGroups);
      idx += cardsPerRow;
    }

    return result;
  }, [cardsPerRow, data]);

  const handleDocPreview = (photo) => {
    showDocumentModal("photo", photo.image, photo, data, refetchData, photo.case_thumbnail);
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {rows.map((rowGroup, rowIndex) => {
        const totalCardsInRow = Object.values(rowGroup).reduce(
          (sum, page) => sum + page.photos.length,
          0
        );

        return (
          <div key={rowIndex} style={{ marginBottom: 5 }}>
            <div style={{ display: "flex", gap: GAP }}>
              {Object.entries(rowGroup).map(([sectionName, page], groupIndex) => {
                const isLastSection = groupIndex === Object.entries(rowGroup).length - 1;

                let cardsBeforeThisSection = 0;
                Object.entries(rowGroup)
                  .slice(0, groupIndex)
                  .forEach(([, p]) => {
                    cardsBeforeThisSection += p?.photos?.length;
                  });

                const cardsInThisSection = page.photos.length;
                const shouldExpandHeader = isLastSection && totalCardsInRow < cardsPerRow;
                const headerWidth = shouldExpandHeader
                  ? CARD_WIDTH * cardsInThisSection +
                    GAP * (cardsInThisSection - 1) +
                    (CARD_WIDTH + GAP) * (cardsPerRow - totalCardsInRow) +
                    GAP
                  : CARD_WIDTH * cardsInThisSection + GAP * (cardsInThisSection - 1);

                return (
                  <div key={groupIndex} style={{ display: "flex", flexDirection: "column" }}>
                    <HeaderSection name={sectionName} icon={page.icon} width={headerWidth} />

                    <div style={{ display: "flex", gap: GAP }}>
                      {page?.photos.map((photo, photoIndex) => {
                        const cardPositionInRow = cardsBeforeThisSection + photoIndex;
                        return (
                          <PhotoDetailedCard
                            key={photo.id}
                            photo={photo}
                            slot={photo?.photo_slot}
                            handleDocPreview={handleDocPreview}
                            cardWidth={CARD_WIDTH}
                            rowIndex={rowIndex}
                            cardPositionInRow={cardPositionInRow}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NewPhotoDetailedTab;
